/**
 * StackRealtimeProvider — unified WebSocket + REST data layer for StackDetailsPage.
 *
 * Opens exactly 2 Socket.io connections (one per namespace) and provides
 * all realtime data via React context. Components consume slices via
 * `useStackRealtime()`.
 *
 * Benefits over individual hooks:
 * - Reduces 5 WS connections → 2 (3 to /deployment-pipeline-events, 2 to /pods)
 * - Centralizes REST polling fallbacks (10s when disconnected)
 * - Shares `selectedRunId` across tabs for run-scoped filtering
 * - Eliminates duplicate /metrics polling (useStackStatus 10s + usePollingApi 30s → 1 × 30s)
 *
 * Hooks that consume this context (with backward-compatible fallback):
 * - useDeploymentStream → events, pipelineState, logBatches
 * - useStackStatus → status, deploymentUrl
 * - usePodMonitor → podSnapshot
 * - useK8sEvents → k8sEvents
 * - useGitOpsStatus → gitopsStatus
 */

import React, { createContext, useContext, useMemo } from "react";

import type { StackMetrics } from "../pages/StackDetailsPage/types";

import type { DeploymentEvent } from "./useDeploymentEvents";
import type { PipelineStep } from "./useDeploymentStream";
import type { GitOpsStatus } from "./useGitOpsStatus";
import type { K8sEventSnapshot } from "./useK8sEvents";
import type { PodMonitorSnapshot } from "./usePodMonitor";
import { useStackRealtimePolling } from "./useStackRealtimePolling";
import { useStackRealtimeSse } from "./useStackRealtimeSse";

// ── Context value type ────────────────────────────────────────────────────────

export interface StackRealtimeContextValue {
  // Deployment pipeline
  events: DeploymentEvent[];
  pipelineState: PipelineStep[];
  logBatches: Map<string, Map<string, Map<string, string[]>>>;

  // System status
  status: string | null;
  deploymentUrl: string | null;

  // Pod monitoring
  podSnapshot: PodMonitorSnapshot | null;
  podLoading: boolean;
  podError: string | null;

  // K8s events
  k8sEvents: K8sEventSnapshot[];
  k8sEventsLoading: boolean;
  k8sEventsError: string | null;

  // GitOps status
  gitopsStatus: GitOpsStatus | null;
  gitopsLoading: boolean;
  gitopsError: string | null;

  // Deployment loading/error state
  deploymentLoading: boolean;
  deploymentError: string | null;
  orgMismatch: boolean;
  /** Increments each time a pipeline_run WS event arrives */
  pipelineRunRefreshSignal: number;

  // Metrics (from /metrics endpoint, 30s poll)
  metrics: StackMetrics | null;
  metricsLoading: boolean;

  // Connection state
  deploymentConnected: boolean;
  podsConnected: boolean;

  // Refetch functions
  refetchEvents: () => void;
  refetchPods: () => void;
  refetchK8sEvents: () => void;
  refetchGitOps: () => void;
}

export const StackRealtimeContext =
  createContext<StackRealtimeContextValue | null>(null);

/**
 * Hook to consume the StackRealtime context.
 * Returns null if used outside a StackRealtimeProvider.
 */
export function useStackRealtime(): StackRealtimeContextValue | null {
  return useContext(StackRealtimeContext);
}

// ── Provider component ────────────────────────────────────────────────────────

interface StackRealtimeProviderProps {
  alias: string;
  appEnv: string;
  orgAlias?: string;
  vcsRepoUrl?: string | null;
  children: React.ReactNode;
}

export function StackRealtimeProvider({
  alias,
  appEnv,
  orgAlias: _orgAlias,
  vcsRepoUrl: _vcsRepoUrl,
  children,
}: StackRealtimeProviderProps): React.ReactElement {
  const polling = useStackRealtimePolling(alias, appEnv);
  const { deploymentConnected, podsConnected } = useStackRealtimeSse(
    alias,
    appEnv,
    polling,
  );

  // ── Context value ──────────────────────────────────────────────────────────

  const contextValue = useMemo<StackRealtimeContextValue>(
    () => ({
      events: polling.events,
      pipelineState: polling.pipelineState,
      logBatches: polling.logBatches,
      status: polling.status,
      deploymentUrl: polling.deploymentUrl,
      podSnapshot: polling.podSnapshot,
      podLoading: polling.podLoading,
      podError: polling.podError,
      k8sEvents: polling.k8sEvents,
      k8sEventsLoading: polling.k8sEventsLoading,
      k8sEventsError: polling.k8sEventsError,
      gitopsStatus: polling.gitopsStatus,
      gitopsLoading: polling.gitopsLoading,
      gitopsError: polling.gitopsError,
      deploymentLoading: polling.deploymentLoading,
      deploymentError: polling.deploymentError,
      orgMismatch: polling.orgMismatch,
      metrics: polling.metrics,
      metricsLoading: polling.metricsLoading,
      deploymentConnected,
      podsConnected,
      refetchEvents: polling.fetchDeploymentEvents,
      refetchPods: polling.fetchPods,
      refetchK8sEvents: polling.fetchK8sEvents,
      refetchGitOps: polling.fetchGitOps,
      pipelineRunRefreshSignal: polling.pipelineRunRefreshSignal,
    }),
    [polling, deploymentConnected, podsConnected],
  );

  return (
    <StackRealtimeContext.Provider value={contextValue}>
      {children}
    </StackRealtimeContext.Provider>
  );
}
